import { AnimatePresence } from "framer-motion";
import { useRef } from "react";
import { createPortal } from "react-dom";

import { FloatingChart } from "src/analytics/FloatingChart/FloatingChart";
import {
  FloatingWindowsContext,
  FloatingWindowsContextType,
} from "src/base-components/FloatingWindow/context";
import { useFloatingWindows } from "src/base-components/FloatingWindow/hooks";
import {
  createFloatingWindowsStore,
  FloatingWindowProps,
  FloatingWindowType,
} from "src/base-components/FloatingWindow/store";
import { CaseActionFormWindow } from "src/cases/caseView/components/CaseActionFormWindow";
import { TaskFormFloatingWindow } from "src/cases/caseView/components/TaskFormFloatingWindow";
import { CopilotWindow } from "src/copilot/CopilotWindow";
import { DetailedView } from "src/dataTable/DetailedView/DetailedView";
import { RawJsonSchemaEditorWindow } from "src/jsonSchema/editor/RawJsonSchemaEditorWindow";
import { FlowJsonSchemaWindow } from "src/schema/window/FlowJsonSchemaWindow";
import { ManifestNodeBuilderWindow } from "src/schema/window/ManifestNodeBuilderWindow";

export const FloatingWindowsProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const storeRef = useRef<FloatingWindowsContextType>();

  if (!storeRef.current) {
    storeRef.current = createFloatingWindowsStore();
  }

  return (
    <FloatingWindowsContext.Provider value={storeRef.current}>
      {children}
      <FloatingWindows />
    </FloatingWindowsContext.Provider>
  );
};

const FloatingWindowComponents: Record<
  FloatingWindowType,
  React.FC<FloatingWindowProps>
> = {
  "detailed-view": DetailedView,
  chart: FloatingChart,
  copilot: CopilotWindow,
  "json-schema-editor": RawJsonSchemaEditorWindow,
  "flow-json-schema": FlowJsonSchemaWindow,
  "cm-task": TaskFormFloatingWindow,
  "cm-action": CaseActionFormWindow,
  "manifest-node-builder": ManifestNodeBuilderWindow,
} as const;

const FloatingWindows: React.FC = () => {
  const windows = useFloatingWindows();

  return createPortal(
    <div className="fixed left-0 top-0 isolate z-40">
      <AnimatePresence initial={false}>
        {Object.entries(windows).map(([id, window]) => {
          const Component = FloatingWindowComponents[window.type];

          return (
            <Component
              key={id}
              computePosition={window.computePosition}
              data={window.data}
              id={id}
            />
          );
        })}
      </AnimatePresence>
    </div>,
    document.body,
  );
};
